/**
 * Save Config Button Component
 * Generic save button with loading state and validation
 */

import React from 'react';
import { Button } from '@/core/components/ui/button';
import { Save, AlertCircle } from 'lucide-react';

interface SaveConfigButtonProps {
  /** Callback when save is clicked */
  onSave: () => void;

  /** Whether save is in progress */
  isSaving?: boolean;

  /** Whether there are unsaved changes */
  hasUnsavedChanges?: boolean;

  /** Whether the button is disabled */
  disabled?: boolean;

  /** Optional custom label */
  label?: string;

  /** Optional CSS class */
  className?: string;
}

/**
 * Save Config Button Component
 * Shows save button with appropriate state indicators
 */
export function SaveConfigButton({
  onSave,
  isSaving = false,
  hasUnsavedChanges = false,
  disabled = false,
  label = 'Save Configuration',
  className = '',
}: SaveConfigButtonProps) {
  // Debug logging on every render
  const isButtonDisabled = disabled || isSaving || !hasUnsavedChanges;
  console.log('[SaveConfigButton] Render:', {
    disabled,
    isSaving,
    hasUnsavedChanges,
    isButtonDisabled,
  });

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {hasUnsavedChanges && !isSaving && (
        <div className="flex items-center gap-2 text-sm text-amber-600">
          <AlertCircle className="h-4 w-4" />
          <span>Unsaved changes</span>
        </div>
      )}

      <Button
        type="button"
        onClick={(e) => {
          console.log('[SaveConfigButton] Button clicked!', {
            disabled,
            isSaving,
            hasUnsavedChanges,
            isButtonDisabled,
            event: e,
          });
          onSave();
        }}
        disabled={isButtonDisabled}
        className="min-w-[160px]"
      >
        <Save className="mr-2 h-4 w-4" />
        {isSaving ? 'Saving...' : label}
      </Button>
    </div>
  );
}
